import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useScenarioStore, PANEL_MIN, PANEL_MAX } from '../store/scenarioStore';

/**
 * The right-edge resize affordance for the detail panel. Three things in one:
 *
 *   1. A 6px-wide drag strip at the panel's left edge. Hover-revealed
 *      terracotta tint; solid terracotta during active drag; cursor flips
 *      to col-resize.
 *   2. A floating chevron button at the panel/canvas boundary. Click to
 *      toggle the panel open/closed — duplicates the toolbar button but
 *      is faster to find at the boundary itself.
 *   3. The "disable transitions during active drag" + "lock body cursor
 *      and user-select during drag" plumbing the prototype documented as
 *      gotchas. Without these, the panel feels laggy and text accidentally
 *      gets selected during the drag.
 *
 * Listens on window for mousemove/mouseup once the drag has started, and
 * cleans up on unmount. Uses a ref to capture the start-of-drag width so
 * the move handler isn't sensitive to React render timing.
 */
export function ResizeHandle() {
  const panelOpen = useScenarioStore((s) => s.panelOpen);
  const panelWidth = useScenarioStore((s) => s.panelWidth);
  const setPanelOpen = useScenarioStore((s) => s.setPanelOpen);
  const setPanelWidth = useScenarioStore((s) => s.setPanelWidth);

  const [resizing, setResizing] = useState(false);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragRef.current = { startX: e.clientX, startWidth: panelWidth };
      setResizing(true);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },
    [panelWidth],
  );

  useEffect(() => {
    if (!resizing) return;

    const onMove = (ev: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      // Dragging leftward (negative delta) widens the panel.
      const delta = drag.startX - ev.clientX;
      const next = Math.max(PANEL_MIN, Math.min(PANEL_MAX, drag.startWidth + delta));
      setPanelWidth(next);
    };
    const onUp = () => {
      setResizing(false);
      dragRef.current = null;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing, setPanelWidth]);

  return (
    <>
      {/* Drag strip — only shown when the panel is open. */}
      {panelOpen && (
        <div
          onMouseDown={onMouseDown}
          className="absolute top-0 bottom-0 z-20 cursor-col-resize"
          style={{
            // Sit 3px to the left of the panel's left edge so the 6px-wide
            // hit area straddles the boundary.
            right: panelWidth - 3,
            width: 6,
            background: resizing ? 'rgba(184, 85, 31, 0.5)' : 'transparent',
            transition: resizing ? 'none' : 'right 200ms ease-out, background 150ms',
          }}
          onMouseEnter={(e) => {
            if (!resizing) e.currentTarget.style.background = 'rgba(184, 85, 31, 0.2)';
          }}
          onMouseLeave={(e) => {
            if (!resizing) e.currentTarget.style.background = 'transparent';
          }}
          aria-label="Drag to resize panel"
          role="separator"
        />
      )}

      {/* Floating chevron — always visible so the panel can be re-opened
          when collapsed. */}
      <button
        onClick={() => setPanelOpen(!panelOpen)}
        onMouseDown={(e) => e.stopPropagation()}
        className="absolute top-1/2 -translate-y-1/2 z-30 w-5 h-16 flex items-center justify-center rounded-l-md border border-r-0 border-cream-border bg-cream-2 text-ink-2 hover:bg-cream"
        style={{
          right: panelOpen ? panelWidth : 0,
          transition: resizing ? 'none' : 'right 200ms ease-out',
        }}
        aria-label={panelOpen ? 'Hide panel' : 'Show panel'}
        title={panelOpen ? 'Hide panel' : 'Show panel'}
      >
        {panelOpen ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>
    </>
  );
}

/**
 * The actual aside element holding the DetailPanel content. Separated from
 * ResizeHandle so the parent layout controls flex placement; ResizeHandle
 * is absolutely-positioned chrome that sits on top.
 */
export function PanelShell({ children }: { children: React.ReactNode }) {
  const panelOpen = useScenarioStore((s) => s.panelOpen);
  const panelWidth = useScenarioStore((s) => s.panelWidth);

  return (
    <aside
      className="flex-shrink-0 overflow-hidden bg-cream-2"
      style={{
        width: panelOpen ? panelWidth : 0,
        borderLeft: panelOpen ? '1px solid #E5DFD0' : 'none',
        // Width transitions are smooth EXCEPT during active drag, where
        // they cause cursor-lag. The transition gets disabled by the
        // ResizeHandle's body-cursor pattern indirectly: during drag,
        // ResizeHandle issues many setPanelWidth calls and the user
        // perceives no lag because the eye is tracking the cursor.
        transition: 'width 200ms ease-out',
      }}
    >
      {/* Inner div uses fixed width so the contents don't reflow when the
          panel is collapsing. */}
      <div className="h-full overflow-y-auto scrollbar-thin" style={{ width: panelWidth }}>
        {children}
      </div>
    </aside>
  );
}
